import { Clock3, TicketCheck } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import type { OnboardingStatus } from '@/lib/api/onboarding';

import OnboardingDialog from './components/OnboardingDialog';
import type { OnboardingStep } from './hooks/useOnboardingFlow';
import { useOnboardingFlow } from './hooks/useOnboardingFlow';
import { useOnboardingLauncher } from './store';

/**
 * Mounts the first-login onboarding dialog. All gating (real mode, per-wallet
 * completion record, resume after the OAuth redirect) lives in
 * `useOnboardingFlow`; this component just wires it to the dialog.
 */
/** Seconds left on an active ready check, or null when one is not running. */
function useReadySecondsLeft(state: string, expiresAt: string | null) {
    const [now, setNow] = useState(() => Date.now());
    const running = state === 'ready' && !!expiresAt;
    useEffect(() => {
        if (!running) return;
        const timer = window.setInterval(() => setNow(Date.now()), 1_000);
        return () => window.clearInterval(timer);
    }, [running]);
    if (!running) return null;
    return Math.max(
        0,
        Math.ceil((new Date(expiresAt as string).getTime() - now) / 1_000),
    );
}

/**
 * The minimized pill is the only status a user who closed the dialog ever sees,
 * so it must not read as reassuring while the queue is waiting on them. An
 * `admitted` row with a `lastError` is a failed detached run, and a clean
 * `admitted` row is a lane nobody is using yet: both need the user, not
 * patience.
 */
export function onboardingPillLabel(status: OnboardingStatus): string {
    switch (status.state) {
    case 'queued':
        return `Queue #${status.position}`;
    case 'ready':
        return 'Your lane is ready';
    case 'provisioning':
        return 'Creating wallet';
    case 'admitted':
        return status.lastError
            ? 'Wallet setup needs a retry'
            : 'Continue wallet setup';
    case 'missed':
        return 'Ready check missed';
    default:
        return 'Voucher required';
    }
}

/**
 * Where the pill takes the user. Only the cohort that signs for its own wallets
 * belongs on the provision step: a social user's wallet is created for them, and
 * the provision step would bounce them straight to funding, past the creation
 * progress and the completion the queue step shows them.
 */
export function onboardingPillStep(input: {
    state: OnboardingStatus['state'];
    needsProvisioning: boolean;
}): OnboardingStep {
    return input.needsProvisioning &&
        (input.state === 'admitted' || input.state === 'provisioning')
        ? 'provision'
        : 'voucher';
}

const OnboardingGate: React.FC = () => {
    const flow = useOnboardingFlow();
    const launch = useOnboardingLauncher((state) => state.launch);
    const readySecondsLeft = useReadySecondsLeft(
        flow.onboarding.status.state,
        flow.onboarding.status.readyExpiresAt,
    );

    const queueActive =
        flow.onboarding.config.enabled &&
        !flow.hasCustodialWallet &&
        flow.onboarding.status.state !== 'completed' &&
        flow.onboarding.status.state !== 'disabled';
    const pillLabel = onboardingPillLabel(flow.onboarding.status);
    // a wallet-first user's next move is the ceremony, not the queue; sending
    // them to the queue step only to be bounced hid the failure they were just
    // being told about.
    const pillStep = onboardingPillStep({
        state: flow.onboarding.status.state,
        needsProvisioning: flow.needsProvisioning,
    });

    return (
        <>
            <OnboardingDialog
                open={flow.isOpen}
                onOpenChange={flow.handleOpenChange}
                step={flow.step}
                maxReachedIndex={flow.maxReachedIndex}
                onStepSelect={flow.goToStep}
                isSocialAuthed={flow.isSocialAuthed}
                isWalletAuthed={flow.isWalletAuthed}
                associateSocialProvider={flow.associateSocial?.provider ?? null}
                onWelcomeNext={flow.goToTerms}
                onAcceptTerms={flow.acceptTerms}
                onDeclineTerms={flow.decline}
                onWalletContinue={flow.continueAfterWallet}
                onVoucherContinue={flow.continueAfterVoucher}
                voucher={flow.onboarding}
                needsProvisioning={flow.needsProvisioning}
                provisioning={flow.eoaProvisioning}
                hasCustodialWallet={flow.hasCustodialWallet}
                onProvisionStart={flow.startProvisioning}
                onProvisionSwitchAccount={flow.switchToConnectedAccount}
                onProvisionRejoin={() => void flow.rejoinFromProvision()}
                onProvisionContinue={() => void flow.continueToFunding()}
                provisionContinuing={flow.fundingWindow.isOpening}
                provisionContinueError={flow.fundingWindow.error}
                onFinish={flow.finish}
                socialLink={flow.socialLink}
            />
            {!flow.isOpen && queueActive && (
                <Button
                    variant="secondary"
                    className="fixed bottom-4 right-4 z-40 h-auto rounded-full border border-primary/30 bg-card px-4 py-2 shadow-xl"
                    onClick={() => launch(pillStep)}
                >
                    {flow.onboarding.status.state === 'queued' ? (
                        <Clock3 />
                    ) : (
                        <TicketCheck />
                    )}
                    <span className="flex flex-col items-start leading-tight">
                        <span className="text-xs font-semibold">
                            {pillLabel}
                        </span>
                        {/* the minimized state is exactly where a ready check is
                            easiest to sleep through, so the countdown outranks
                            the queue estimate here */}
                        {readySecondsLeft !== null ? (
                            <span className="text-[10px] font-semibold text-primary">
                                {readySecondsLeft}s to confirm
                            </span>
                        ) : (
                            flow.onboarding.status.estimatedWaitMinutes && (
                                <span className="text-[10px] text-muted-foreground">
                                    {
                                        flow.onboarding.status
                                            .estimatedWaitMinutes.min
                                    }{' '}
                                    to{' '}
                                    {
                                        flow.onboarding.status
                                            .estimatedWaitMinutes.max
                                    }{' '}
                                    min
                                </span>
                            )
                        )}
                    </span>
                </Button>
            )}
        </>
    );
};

export default OnboardingGate;
